import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChatIcon } from "../ui/icons/Icon";
import { TaskWindow } from "../ui/TaskWindow";
import { Toast } from "../ui/feedback";

// Piezas de Chat › Configuración que el portal actual comparte con Equipo (TeamWindow, TeamToast, TeamEmpty,
// OperatingDraftGuard), con el mismo HTML. PENDIENTE para el integrador: el agente de Equipo puede tener su propia copia;
// conviene unificarlas en un solo módulo al integrar.

let windowIds = 0;

/** TeamWindow.razor: ventana de tarea (C30) o confirmación (C12) sobre TaskWindow; Esc, × o el fondo la cierran. */
export function TeamWindow({
  title,
  closeLabel,
  confirm = false,
  footer,
  children,
  open,
  onDismiss,
  stepped = false,
  onBack,
  backLabel,
}: {
  title: string;
  closeLabel: string;
  confirm?: boolean;
  footer?: ReactNode;
  children: ReactNode;
  open: boolean;
  onDismiss: () => void;
  /** C30 con pasos: en el celular se ven ← (paso anterior) y × (cerrar). */
  stepped?: boolean;
  /** ← del celular en una ventana con pasos: vuelve al paso anterior (sin él, cierra). */
  onBack?: () => void;
  backLabel?: string;
}) {
  const [titleId] = useState(() => `tm-window-${(++windowIds).toString(16).padStart(32, "0")}`);
  const dialog = useRef<HTMLDialogElement>(null);
  const opener = useRef<Element | null>(null);
  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  });
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) {
      opener.current = document.activeElement;
      element.showModal();
      // GEN-2 (C30): escritorio -> la ×; celular -> la ← que cierra; confirmación -> su botón secundario. El foco
      // llega por código con preventScroll y :focus-visible no dibuja anillo al abrir con el mouse (C27).
      const target =
        window.innerWidth <= 767
          ? (element.querySelector<HTMLElement>(".tm-back") ??
            element.querySelector<HTMLElement>("[data-tm-close]") ??
            element.querySelector<HTMLElement>("footer .tm-btn.secondary, footer button"))
          : (element.querySelector<HTMLElement>("[data-tm-close]") ??
            element.querySelector<HTMLElement>("footer .tm-btn.secondary, footer button"));
      target?.focus({ preventScroll: true });
    } else if (!open && element.open) {
      element.close();
      if (opener.current instanceof HTMLElement && opener.current.isConnected)
        opener.current.focus({ preventScroll: true });
    }
  }, [open]);
  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const abort = new AbortController();
    element.addEventListener(
      "cancel",
      (event) => {
        event.preventDefault();
        dismiss.current();
      },
      { signal: abort.signal },
    );
    element.addEventListener(
      "pointerdown",
      (event) => {
        if (event.target !== element) return;
        const r = element.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)
          dismiss.current();
      },
      { signal: abort.signal },
    );
    return () => abort.abort();
  }, []);
  return (
    <TaskWindow
      ref={dialog}
      className={confirm ? "tm-window tm-confirm" : stepped ? "tm-window tm-stepped" : "tm-window"}
      titleId={titleId}
    >
      <header>
        {!confirm ? (
          <button
            type="button"
            className="tm-back"
            aria-label={onBack ? (backLabel ?? closeLabel) : closeLabel}
            title={onBack ? (backLabel ?? closeLabel) : closeLabel}
            onClick={() => (onBack ? onBack() : dismiss.current())}
          >
            <ChatIcon name="arrow-left" />
          </button>
        ) : null}
        <h2 id={titleId}>{title}</h2>
        {!confirm ? (
          <button
            type="button"
            className="tm-icon-btn"
            data-tm-close=""
            aria-label={closeLabel}
            title={closeLabel}
            onClick={() => dismiss.current()}
          >
            <ChatIcon name="x" />
          </button>
        ) : null}
      </header>
      <div className="tm-window-body">{children}</div>
      {footer ? <footer>{footer}</footer> : null}
    </TaskWindow>
  );
}

/** TeamEmpty.razor: vacío centrado con su acción. */
export function TeamEmpty({
  title,
  line,
  actionLabel,
  actionHref,
}: {
  title: string;
  line?: string;
  actionLabel?: string;
  actionHref?: string;
}) {
  return (
    <div className="tm-empty" role="status" data-part="vacio">
      <strong>{title}</strong>
      {line ? <span>{line}</span> : null}
      {actionLabel && actionHref ? (
        <a className="tm-btn secondary" href={actionHref}>
          {actionLabel}
        </a>
      ) : null}
    </div>
  );
}

/** TeamToast.razor: un solo aviso (el nuevo reemplaza al anterior), 2,8 s. */
export function useTeamToast(): [ReactNode, (message: string) => void] {
  const [toast, setToast] = useState<{ message: string; version: number } | null>(null);
  const version = useRef(0);
  const show = (message: string) => {
    const current = ++version.current;
    setToast({ message, version: current });
    window.setTimeout(() => {
      if (version.current === current) setToast(null);
    }, 2800);
  };
  const node = toast ? <Toast key={toast.version} message={toast.message} tone="success" action={<></>} /> : null;
  return [node, show];
}

/**
 * OperatingDraftGuard.razor: con cambios sin guardar, salir por un enlace o cerrar la pestaña pregunta «¿Descartar los
 * cambios?» («Seguir editando» · «Descartar»).
 */
export function DraftGuard({ dirty, L }: { dirty: boolean; L: (key: string) => string }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [id] = useState(() => `cfg1-leave-${(++windowIds).toString(16).padStart(32, "0")}`);
  const state = useRef({ dirty });
  useEffect(() => {
    state.current.dirty = dirty;
  });
  useEffect(() => {
    const abort = new AbortController();
    const confirmLeave = (): Promise<boolean> =>
      new Promise((resolve) => {
        const element = dialog.current!;
        const trigger = document.activeElement as HTMLElement | null;
        const finish = (value: boolean) => {
          element.removeEventListener("click", click);
          element.removeEventListener("cancel", cancel);
          if (element.open) element.close();
          if (trigger?.isConnected) trigger.focus();
          resolve(value);
        };
        const click = (event: MouseEvent) => {
          if ((event.target as Element).closest("[data-cfg1-leave]")) finish(true);
          else if ((event.target as Element).closest("[data-cfg1-stay]")) finish(false);
        };
        const cancel = (event: Event) => {
          event.preventDefault();
          finish(false);
        };
        element.addEventListener("click", click);
        element.addEventListener("cancel", cancel);
        element.showModal();
        element.querySelector<HTMLElement>("[data-cfg1-stay]")?.focus();
      });
    document.addEventListener(
      "click",
      (event) => {
        if (
          !state.current.dirty ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        const anchor = (event.target as Element).closest?.("a[href]") as HTMLAnchorElement | null;
        if (!anchor || (anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;
        const target = new URL(anchor.href, location.href);
        if (target.pathname === location.pathname && target.search === location.search) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        void confirmLeave().then((leave) => {
          if (!leave) return;
          state.current.dirty = false;
          anchor.click();
        });
      },
      { capture: true, signal: abort.signal },
    );
    window.addEventListener(
      "beforeunload",
      (event) => {
        if (state.current.dirty) event.preventDefault();
      },
      { signal: abort.signal },
    );
    return () => abort.abort();
  }, []);
  return (
    <dialog ref={dialog} className="confirmation-dialog tm-discard" aria-labelledby={`${id}-title`}>
      <h2 id={`${id}-title`}>{L("CFG1_R1_DISCARD_TITLE")}</h2>
      <div className="confirmation-actions">
        <button type="button" className="tm-btn secondary" data-cfg1-stay="">
          {L("CFG1_STAY")}
        </button>
        <button type="button" className="tm-btn primary" data-cfg1-leave="">
          {L("CFG1_R1_DISCARD")}
        </button>
      </div>
    </dialog>
  );
}
